import { FileText } from "lucide-react";
import { useState } from "react";
import { cn } from "../../../lib/utils";

/**
 * Cover for an HTML card whose live document hasn't painted yet: the
 * server-rendered preview JPEG when one is usable, else the file glyph.
 * Shared by the block card and the legacy thumb card so their loading states
 * cannot drift.
 *
 * - `onError` demotes a dead preview URL (expired or pruned object) to the
 *   glyph instead of leaving an invisible broken `<img>` up for the whole
 *   progressive-mount wait.
 * - `pointer-events-none` throughout: the placeholder overlays the shield and
 *   the Open affordance and must never swallow their clicks.
 * - `showGlyph` marks surfaces where an opaque plate is acceptable. Frameless
 *   blocks pass false so an absent/failed preview degrades to nothing — an
 *   opaque box under a chromeless block reads as a bug.
 */
export function HtmlCardPlaceholder({
  previewImageUrl,
  showGlyph,
}: {
  previewImageUrl: string | null;
  showGlyph: boolean;
}) {
  const [previewFailed, setPreviewFailed] = useState(false);

  if (previewImageUrl && !previewFailed) {
    return (
      <img
        src={previewImageUrl}
        alt=""
        aria-hidden
        draggable={false}
        loading="lazy"
        decoding="async"
        onError={() => setPreviewFailed(true)}
        className={cn(
          "pointer-events-none absolute inset-0 z-10 h-full w-full object-cover object-top",
          // Framed cards keep the plate under the image so a lazy-deferred or
          // still-decoding JPEG isn't a transparent hole.
          showGlyph && "bg-muted",
        )}
      />
    );
  }
  if (!showGlyph) return null;
  return (
    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-muted">
      <FileText className="h-8 w-8 text-muted-foreground" />
    </div>
  );
}
